import { ErrorHandler, Inject, NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { appConfig, APP_CONFIG } from './models/constants';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { CookieService } from 'ngx-cookie-service';
import { TokenInterceptor } from './services/interceptors/token.interceptor';
import { QueryParameterInterceptor } from './services/interceptors/query-parameter.interceptor';
import { HttpRequestCancelInterceptor } from './services/interceptors/httprequestcancel.interceptor';
import { AutoLogoutService } from './services/auto-logout.service';
import { HttpCancelRequestService } from './services/http-cancel-request.service';
import { EncryptDecryptService } from './services/encrypt-decrypt.service';
import { BnNgIdleService } from 'bn-ng-idle';
import { PreloginComponent } from './prelogin/prelogin.component';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePipe } from '@angular/common';
import { NgHttpLoaderModule } from 'ng-http-loader';
import { ApplicationInsightsErrorHandler } from './services/app-insights/appInsights-errorhandler.service';
import { DialogService } from 'primeng/dynamicdialog';
import { KsaInterceptor } from './services/interceptors/ksa.interceptor';

import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent, MsalGuard, MsalBroadcastService } from '@azure/msal-angular'
import { MSALAuthGuard } from './services/auth-guard/msalauth-Guard-service';
import { AuthGuard } from './services/auth-guard/auth-guard-service';
import { PreAuthGuard } from './services/auth-guard/preauth.guard';

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1; // Remove this line to use Angular Universal

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: appConfig?.appConfigService.azureAdConfig?.clientId,
      authority: appConfig?.appConfigService.azureAdConfig?.authority,
      redirectUri: appConfig?.appConfigService.azureAdConfig?.redirectUri,
      postLogoutRedirectUri: appConfig?.appConfigService.azureAdConfig?.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: isIE,
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(appConfig.appConfigService.baseApiUrl, ['user.read', 'profile']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      redirectUri: appConfig?.appConfigService.azureAdConfig?.redirectUri,
      authority: appConfig?.appConfigService.azureAdConfig?.authority,
      scopes: ['user.read']
    },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    PreloginComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    MsalModule,
    BrowserAnimationsModule,
    DialogModule,
    ConfirmDialogModule,
    NgHttpLoaderModule.forRoot()
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KsaInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: QueryParameterInterceptor,
      multi: true,
    },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestCancelInterceptor,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: ErrorHandler,
      useClass: ApplicationInsightsErrorHandler,
    },
    MsalGuard,
    MsalService,
    MsalBroadcastService,
    AutoLogoutService,
    ConfirmationService,
    HttpCancelRequestService,
    CookieService,
    EncryptDecryptService,
    BnNgIdleService,
    DatePipe,
    MSALAuthGuard,
    AuthGuard,
    PreAuthGuard,
    ConfirmationService, DialogService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})

export class AppModule {
  constructor(@Inject(APP_CONFIG) public configData) {
    appConfig.appConfigService = configData;
    appConfig.updateConstant(configData);
  }
}
